import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Button, Card, Dialog, ErrorState, Field, Input, Loading, Select } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { number } from '../../lib/format.js';

/**
 * How requirements turn into leads.
 *
 * A tenant posts a requirement; the matching engine scores it against brokers'
 * areas and listings, and offers it as a lead to the best matches. Every number
 * on this screen is read on the next requirement posted or matched, so like the
 * credit rates it saves through a confirmation listing exactly what moves.
 *
 * Prices live under Credits → Rates; this screen holds the rules, not the costs.
 */

const RULES = [
  {
    section: 'broker',
    key: 'requirementExpiryDays',
    label: 'A requirement stays live for',
    unit: 'days',
    min: 1,
    max: 365,
    hint: 'After this, brokers stop seeing it and the tenant is asked to post again.',
  },
  {
    section: 'broker',
    key: 'maxLeadsPerRequirement',
    label: 'Brokers per requirement, at most',
    unit: 'brokers',
    min: 1,
    max: 50,
    hint: 'The website promises tenants "no more than eight". Change that text too if this moves.',
  },
  {
    section: 'broker',
    key: 'minMatchScoreToCreateLead',
    label: 'Minimum match score for a lead',
    unit: 'out of 100',
    min: 0,
    max: 100,
    hint: 'Brokers below this score are not offered the requirement.',
  },
];

const TOLERANCES = [
  {
    section: 'matching',
    key: 'locationRadiusKm',
    label: 'Search radius',
    unit: 'km',
    min: 1,
    max: 100,
    hint: 'Used when a requirement does not set its own.',
  },
  {
    section: 'matching',
    key: 'budgetTolerancePercent',
    label: 'Over-budget allowance',
    unit: '%',
    min: 0,
    max: 100,
    hint: 'How far above the tenant’s maximum rent a listing can be and still count.',
  },
  {
    section: 'matching',
    key: 'moveInToleranceDays',
    label: 'Move-in date allowance',
    unit: 'days',
    min: 0,
    max: 180,
    hint: 'How far apart the move-in date and the availability date can be.',
  },
];

const WEIGHTS = [
  { key: 'location', label: 'Location' },
  { key: 'budget', label: 'Budget' },
  { key: 'bedrooms', label: 'Bedrooms' },
  { key: 'propertyType', label: 'Property type' },
  { key: 'furnishing', label: 'Furnishing' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'moveInDate', label: 'Move-in date' },
];

/*
 * The oldest Android build of each app still allowed to open. Below it the app
 * shows a blocking "update required" screen; 0 lets every build through.
 */
const APP_VERSIONS = [
  {
    section: 'appVersions',
    key: 'rentalMinVersionCode',
    label: 'Oldest rental app build allowed',
    unit: 'versionCode',
    min: 0,
    max: 1000000,
    hint: 'The Play versionCode, shown in Play Console under App bundle explorer. Older builds must update before they open. 0 allows every build.',
  },
  {
    section: 'appVersions',
    key: 'brokerMinVersionCode',
    label: 'Oldest broker app build allowed',
    unit: 'versionCode',
    min: 0,
    max: 1000000,
    hint: 'Same, for the broker app. Raise it only once the newer build is live on Google Play, or nobody can update.',
  },
];

const NUMBER_FIELDS = [...RULES, ...TOLERANCES, ...APP_VERSIONS];

const isWhole = (value) => String(value ?? '').trim() !== '' && Number.isInteger(Number(value));

function draftFrom(config) {
  return {
    broker: { ...config.broker },
    matching: { ...config.matching, weights: { ...config.matching?.weights } },
    appVersions: { rentalMinVersionCode: 0, brokerMinVersionCode: 0, ...config.appVersions },
  };
}

export default function SettingsPage() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CONFIG_MANAGE);
  const queryClient = useQueryClient();

  const config = useQuery({ queryKey: ['admin-platform-config'], queryFn: api.config.get });
  // `null` until the first keystroke, so a refetch shows the server's values.
  const [edits, setEdits] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: (patch) => api.config.update(patch),
    onSuccess: () => {
      toast.success('Settings saved.');
      queryClient.invalidateQueries({ queryKey: ['admin-platform-config'] });
      setConfirming(false);
      setEdits(null);
    },
    onError: (error) => toast.error(error.message),
  });

  if (config.isLoading) {
    return (
      <Page title="Settings">
        <Loading label="Loading settings" />
      </Page>
    );
  }
  if (config.isError) {
    return (
      <Page title="Settings">
        <ErrorState error={config.error} onRetry={config.refetch} />
      </Page>
    );
  }

  const server = config.data;
  const draft = edits ?? draftFrom(server);
  const update = (change) => setEdits((current) => change(current ?? draftFrom(server)));

  const setValue = (section, key, value) => {
    update((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
    setErrors((current) => ({ ...current, [key]: null }));
  };
  const setWeight = (key, value) => {
    update((current) => ({
      ...current,
      matching: { ...current.matching, weights: { ...current.matching.weights, [key]: value } },
    }));
    setErrors((current) => ({ ...current, weights: null }));
  };

  /** Old → new for everything that moved; the patch carries only those. */
  const rows = [];
  const patch = {};
  NUMBER_FIELDS.forEach(({ section, key, label, unit }) => {
    const next = Number(draft[section][key]);
    const current = Number(server[section]?.[key] ?? 0);
    if (next !== current) {
      patch[section] = { ...patch[section], [key]: next };
      rows.push({ label, from: current, to: next, unit });
    }
  });
  if (Boolean(draft.broker.requireBrokerVerification) !== Boolean(server.broker?.requireBrokerVerification)) {
    patch.broker = { ...patch.broker, requireBrokerVerification: Boolean(draft.broker.requireBrokerVerification) };
    rows.push({
      label: 'Only verified brokers get leads',
      from: server.broker?.requireBrokerVerification ? 'Yes' : 'No',
      to: draft.broker.requireBrokerVerification ? 'Yes' : 'No',
    });
  }
  const weightsChanged = WEIGHTS.some(
    ({ key }) => Number(draft.matching.weights[key]) !== Number(server.matching?.weights?.[key]),
  );
  if (weightsChanged) {
    // All seven together: the server replaces the weights as a set.
    patch.matching = {
      ...patch.matching,
      weights: Object.fromEntries(WEIGHTS.map(({ key }) => [key, Number(draft.matching.weights[key])])),
    };
    WEIGHTS.forEach(({ key, label }) => {
      if (Number(draft.matching.weights[key]) !== Number(server.matching?.weights?.[key])) {
        rows.push({ label: `Weight · ${label}`, from: server.matching?.weights?.[key], to: Number(draft.matching.weights[key]) });
      }
    });
  }

  const weightTotal = WEIGHTS.reduce((sum, { key }) => sum + (Number(draft.matching.weights[key]) || 0), 0);

  const validate = () => {
    const found = {};
    NUMBER_FIELDS.forEach(({ section, key, label, min, max }) => {
      const value = draft[section][key];
      if (!isWhole(value) || Number(value) < min || Number(value) > max) {
        found[key] = `${label} must be a whole number from ${min} to ${max}.`;
      }
    });
    if (WEIGHTS.some(({ key }) => !isWhole(draft.matching.weights[key]) || Number(draft.matching.weights[key]) < 0 || Number(draft.matching.weights[key]) > 100)) {
      found.weights = 'Each weight must be a whole number from 0 to 100.';
    } else if (weightTotal === 0) {
      found.weights = 'At least one weight must be above zero.';
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const review = () => {
    if (!validate()) return;
    if (!rows.length) {
      toast.success('Nothing to save — these are the settings already in force.');
      return;
    }
    setConfirming(true);
  };

  const numberField = ({ section, key, label, unit, hint }) => (
    <Field key={key} label={`${label} (${unit})`} hint={hint} error={errors[key]} id={`setting-${key}`}>
      <Input
        id={`setting-${key}`}
        type="text"
        inputMode="numeric"
        disabled={!canManage}
        error={errors[key]}
        value={draft[section][key] ?? ''}
        onChange={(event) => setValue(section, key, event.target.value)}
      />
    </Field>
  );

  return (
    <Page
      title="Settings"
      subtitle="How a tenant's requirement reaches brokers, and which app builds may run. Prices are under Credits → Rates."
      actions={
        canManage ? (
          <>
            <Button variant="ghost" disabled={!rows.length} onClick={() => { setEdits(null); setErrors({}); }}>
              Discard
            </Button>
            <Button variant="primary" disabled={!rows.length} onClick={review}>
              {rows.length ? `Review ${rows.length} change${rows.length === 1 ? '' : 's'}` : 'Save'}
            </Button>
          </>
        ) : null
      }
    >
      <div className="stack">
        <Card title="Requirements and leads" subtitle="Who is offered a tenant's requirement, and for how long.">
          <div className="stack">
            {RULES.map(numberField)}
            <Field
              label="Only verified brokers get leads"
              hint="When on, an agency must pass document verification before it is offered requirements. Unverified agencies can still list; their listings wait for review."
              id="setting-requireBrokerVerification"
            >
              <Select
                id="setting-requireBrokerVerification"
                disabled={!canManage}
                value={draft.broker.requireBrokerVerification ? 'yes' : 'no'}
                onChange={(event) => setValue('broker', 'requireBrokerVerification', event.target.value === 'yes')}
              >
                <option value="yes">Yes — verified agencies only</option>
                <option value="no">No — any agency</option>
              </Select>
            </Field>
          </div>
        </Card>

        <Card title="Matching allowances" subtitle="How close a listing or broker has to be to count as a match.">
          <div className="stack">{TOLERANCES.map(numberField)}</div>
        </Card>

        <Card
          title="Match score weights"
          subtitle={`How much each criterion counts towards the score out of 100. They are relative — currently totalling ${number(weightTotal)}.`}
        >
          <div className="field-row">
            {WEIGHTS.map(({ key, label }) => (
              <Field key={key} label={label} id={`weight-${key}`}>
                <Input
                  id={`weight-${key}`}
                  type="text"
                  inputMode="numeric"
                  disabled={!canManage}
                  error={errors.weights}
                  value={draft.matching.weights[key] ?? ''}
                  onChange={(event) => setWeight(key, event.target.value)}
                />
              </Field>
            ))}
          </div>
          {errors.weights ? <p className="field__error" role="alert">{errors.weights}</p> : null}
        </Card>

        <Card
          title="App versions"
          subtitle="Force an update. Phones on an older Android build see a blocking “update required” screen the next time they open the app."
        >
          <div className="stack">{APP_VERSIONS.map(numberField)}</div>
        </Card>
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Save these settings?"
        warning="Live as soon as it saves. Matching changes apply to the next requirement posted or matched; leads already offered are not revisited. A raised app version blocks older builds on their next launch until they update. Recorded in the audit log against your account."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(patch)}>
              Save settings
            </Button>
          </>
        }
      >
        <ul className="stack stack--sm">
          {rows.map((row) => (
            <li key={row.label}>
              <strong>{row.label}</strong>
              {`: ${row.from ?? '—'} → ${row.to}${row.unit ? ` ${row.unit}` : ''}`}
            </li>
          ))}
        </ul>
      </Dialog>
    </Page>
  );
}
